/**
 * PersonalInfoTab - Tab pentru informații personale în ProfilePage
 * 
 * Conține:
 * - Informații de bază (email, rol, nume, prenume, telefon)
 * - Cod parafă (doar medici)
 * - Modalități (doar medici)
 * - Upload parafă (doar medici)
 * - Schimbare parolă
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Star as StarIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/api';
import { UpdateUserProfile, ChangePasswordRequest } from '../../types/api';
import apiClient from '../../services/api';

// Opțiuni pentru modalități
const MODALITIES = [
  'CT', 'MR', 'DX', 'US', 'XA', 'CR', 'MG', 'NM', 'PT', 'ES', 'RF', 'OT'
];

interface PersonalInfoTabProps {
  onSnackbar: (message: string, severity: 'success' | 'error') => void;
}

const PersonalInfoTab: React.FC<PersonalInfoTabProps> = ({ onSnackbar }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  
  // Form state pentru editare
  const [formData, setFormData] = useState<UpdateUserProfile | null>(null);
  
  // Password change state
  const [passwordData, setPasswordData] = useState<ChangePasswordRequest>({
    current_password: '',
    new_password: '',
  });
  
  // State pentru parafă
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const profileData = await userService.getUserProfile(user.id);
      setProfile(profileData);
      setFormData(profileData as UpdateUserProfile);
    } catch (error) {
      console.error('Eroare la încărcarea profilului:', error);
      onSnackbar('Eroare la încărcarea profilului', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData(profile as UpdateUserProfile);
  };

  const handleSave = async () => {
    if (!user || !formData) return;

    try {
      setSaving(true);
      const updatedProfile = await userService.updateUserProfile(user.id, formData);
      setProfile(updatedProfile);
      setEditing(false);
      onSnackbar('Profil actualizat cu succes!', 'success');
    } catch (error) {
      console.error('Eroare la actualizarea profilului:', error);
      onSnackbar('Eroare la actualizarea profilului', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;

    try {
      setSaving(true);
      await userService.changePassword(user.id, passwordData);
      setPasswordDialog(false);
      setPasswordData({ current_password: '', new_password: '' });
      onSnackbar('Parolă schimbată cu succes!', 'success');
    } catch (error) {
      console.error('Eroare la schimbarea parolei:', error);
      onSnackbar('Eroare la schimbarea parolei', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (!formData) return;
    
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleModalitiesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    handleInputChange('modalities', typeof value === 'string' ? value.split(',') : value);
  };

  const getRoleDisplayName = (role: string) => {
    const roleNames: { [key: string]: string } = {
      admin: 'Administrator',
      medic: 'Medic',
      operator: 'Operator',
      tehnician: 'Tehnician',
      coordinator: 'Coordinator'
    };
    return roleNames[role] || role;
  };

  // Funcții pentru gestionarea parafei
  const handleSignatureFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        onSnackbar('Vă rugăm să selectați un fișier imagine (PNG, JPG, JPEG)', 'error');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        onSnackbar('Fișierul este prea mare. Dimensiunea maximă este 5MB', 'error');
        return;
      }
      
      setSignatureFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setSignaturePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadSignature = async () => {
    if (!signatureFile) return;
    
    setSignatureUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', signatureFile);
      
      await apiClient.post('/users/doctor/signature/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      onSnackbar('Parafa a fost uploadată cu succes!', 'success');
      loadProfile();
      setSignatureFile(null);
      setSignaturePreview(null);
      
    } catch (error: any) {
      console.error('Eroare la uploadul parafei:', error);
      onSnackbar(error.response?.data?.detail || 'Eroare la uploadul parafei', 'error');
    } finally {
      setSignatureUploading(false);
    }
  };

  const handleDeleteSignature = async () => {
    setSignatureUploading(true);
    try {
      await apiClient.delete('/users/doctor/signature');
      onSnackbar('Parafa a fost ștearsă cu succes!', 'success');
      loadProfile();
    } catch (error: any) {
      console.error('Eroare la ștergerea parafei:', error);
      onSnackbar(error.response?.data?.detail || 'Eroare la ștergerea parafei', 'error');
    } finally {
      setSignatureUploading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Compact styles
  const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } };
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    },
  };

  if (!user || !profile) {
    return null;
  }

  return (
    <Grid container spacing={1.5}>
      {/* Informații de bază */}
      <Grid item xs={12} md={8}>
        <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Informații Personale</Typography>
              {!editing ? (
                <Button startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={handleEdit} variant="outlined" size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Editează</Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button startIcon={<SaveIcon sx={{ fontSize: 14 }} />} onClick={handleSave} variant="contained" disabled={saving} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>{saving ? <CircularProgress size={14} /> : 'Salvează'}</Button>
                  <Button startIcon={<CancelIcon sx={{ fontSize: 14 }} />} onClick={handleCancel} variant="outlined" size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Anulează</Button>
                </Box>
              )}
            </Box>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" value={user.email} disabled size="small" sx={inputSx} InputProps={{ startAdornment: <PersonIcon sx={{ mr: 0.5, fontSize: 16, color: 'action.disabled' }} /> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Rol" value={getRoleDisplayName(user.role)} disabled size="small" sx={inputSx} InputProps={{ startAdornment: <WorkIcon sx={{ mr: 0.5, fontSize: 16, color: 'action.disabled' }} /> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Nume" value={formData?.first_name || ''} onChange={(e) => handleInputChange('first_name', e.target.value)} disabled={!editing} size="small" sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Prenume" value={formData?.last_name || ''} onChange={(e) => handleInputChange('last_name', e.target.value)} disabled={!editing} size="small" sx={inputSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Telefon" value={formData?.phone || ''} onChange={(e) => handleInputChange('phone', e.target.value)} disabled={!editing} size="small" sx={inputSx} InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 0.5, fontSize: 16, color: 'action.disabled' }} /> }} />
              </Grid>
              {(user.role === 'medic' || user.role === 'coordinator') && (
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Cod Parafă" value={(formData as any)?.cod_parafa || ''} onChange={(e) => handleInputChange('cod_parafa', e.target.value)} disabled={!editing} size="small" sx={inputSx} />
                </Grid>
              )}
              {(user.role === 'medic' || user.role === 'coordinator') && (
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Scor Performanță" value={(() => { const score = (profile as any)?.score_cached; if (!score) return '3.0'; const numScore = typeof score === 'string' ? parseFloat(score) : score; return isNaN(numScore) ? '3.0' : numScore.toFixed(1); })()} disabled size="small" sx={inputSx} InputProps={{ startAdornment: <StarIcon sx={{ mr: 0.5, fontSize: 16, color: 'action.disabled' }} /> }} />
                </Grid>
              )}
              {(user.role === 'medic' || user.role === 'coordinator') && (
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!editing} size="small" sx={inputSx}>
                    <InputLabel>Modalități</InputLabel>
                    <Select multiple value={(formData as any)?.modalities || []} onChange={handleModalitiesChange} input={<OutlinedInput label="Modalități" />} renderValue={(selected) => (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>{selected.map((value) => (<Chip key={value} label={value} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />))}</Box>)}>
                      {MODALITIES.map((modality) => (<MenuItem key={modality} value={modality} sx={{ fontSize: '0.8rem' }}>{modality}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Sidebar cu acțiuni */}
      <Grid item xs={12} md={4}>
        <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>Acțiuni</Typography>
            <Button fullWidth variant="outlined" startIcon={<LockIcon sx={{ fontSize: 14 }} />} onClick={() => setPasswordDialog(true)} size="small" sx={{ mb: 1, height: 30, fontSize: '0.75rem', textTransform: 'none' }}>Schimbă Parola</Button>

            {(user.role === 'medic' || user.role === 'coordinator') && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>Parafă Medicală</Typography>
                {(profile as any)?.signature_base64 && (
                  <Box sx={{ mb: 1, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>Parafa curentă:</Typography>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5, bgcolor: 'background.paper', display: 'inline-block' }}>
                      <Box component="img" src={(profile as any).signature_base64} alt="Parafa curentă" sx={{ maxWidth: '150px', maxHeight: '60px', objectFit: 'contain', display: 'block' }} />
                    </Box>
                  </Box>
                )}
                {signaturePreview && (
                  <Box sx={{ mb: 1, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>Preview:</Typography>
                    <Box component="img" src={signaturePreview} alt="Preview parafă" sx={{ maxWidth: '150px', maxHeight: '60px', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5, bgcolor: 'background.paper' }} />
                  </Box>
                )}
                <input accept="image/*" style={{ display: 'none' }} id="signature-upload" type="file" onChange={handleSignatureFileChange} />
                <label htmlFor="signature-upload">
                  <Button variant="outlined" component="span" startIcon={<UploadIcon sx={{ fontSize: 14 }} />} fullWidth size="small" sx={{ mb: 0.5, height: 28, fontSize: '0.72rem', textTransform: 'none' }} disabled={signatureUploading}>{signatureFile ? 'Schimbă' : 'Uploadează'}</Button>
                </label>
                {signatureFile && <Button variant="contained" onClick={handleUploadSignature} disabled={signatureUploading} fullWidth size="small" sx={{ mb: 0.5, height: 28, fontSize: '0.72rem', textTransform: 'none' }}>{signatureUploading ? <CircularProgress size={14} /> : 'Salvează Parafa'}</Button>}
                {(profile as any)?.signature_base64 && <Button variant="outlined" color="error" startIcon={<DeleteIcon sx={{ fontSize: 14 }} />} onClick={handleDeleteSignature} disabled={signatureUploading} fullWidth size="small" sx={{ mb: 0.5, height: 28, fontSize: '0.72rem', textTransform: 'none' }}>{signatureUploading ? <CircularProgress size={14} /> : 'Șterge'}</Button>}
                <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', mt: 0.5 }}>Parafa va fi redimensionată la 300x150px.</Typography>
              </>
            )}

            <Divider sx={{ my: 1 }} />
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}><strong>Status:</strong> {user.active ? 'Activ' : 'Inactiv'}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}><strong>Creat:</strong> {user.created_at ? (() => { try { const date = new Date(user.created_at); if (isNaN(date.getTime())) return 'N/A'; return date.toLocaleDateString('ro-RO'); } catch { return 'N/A'; } })() : 'N/A'}</Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Dialog pentru schimbare parolă - Compact & Consistent */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>Schimbă Parola</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
            <TextField fullWidth type="password" label="Parola Actuală" value={passwordData.current_password} onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })} size="small" sx={inputSx} />
            <TextField fullWidth type="password" label="Parola Nouă" value={passwordData.new_password} onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })} size="small" sx={inputSx} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setPasswordDialog(false)} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Anulează</Button>
          <Button onClick={handleChangePassword} variant="contained" disabled={saving || !passwordData.current_password || !passwordData.new_password} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>{saving ? <CircularProgress size={14} /> : 'Schimbă'}</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default PersonalInfoTab;

